/**
 * The kick of your own shot. Janeks, 2026-10-19: *"On shot - slightly shake
 * the screen. (The aim and cursor dont move) slightly. Also can add a blur
 * effect on shot faded towards center. Mostly corners only."*
 *
 * ─── Why the shake is a VIEW OFFSET, applied around the render call only ─────
 * Moving the camera would move everything that reads it: the aim ray from the
 * pointer, the ground point the character turns to, the name tags. So the
 * camera itself never moves. `beforeRender` shifts the projection a few pixels
 * (`setViewOffset`) for the one `renderer.render` call, and `afterRender`
 * clears it again before anything else in the frame can see it. The picture
 * kicks; the aim, the cursor and every raycast stay exactly where they were.
 *
 * ─── The blur ───────────────────────────────────────────────────────────────
 * A DOM layer over the canvas with `backdrop-filter: blur`, masked by a radial
 * gradient that is clear in the middle and solid at the corners, faded in on
 * the shot and out over a fraction of a second. Pointer-events none, so it
 * never takes a click.
 *
 * Both are off under prefers-reduced-motion.
 */
import type * as THREE from 'three';

/** Peak shake, in CSS pixels. "Slightly". */
export const SHOT_SHAKE_PX = 3;
/** How long one kick takes to settle. */
export const SHOT_SHAKE_MS = 140;
/** How long the corner blur takes to fade out. */
export const SHOT_BLUR_MS = 260;

export interface ShotFeedback {
  /** One shot. `strength` 0..1 scales the kick (a pistol is lighter than a rifle). */
  kick(strength?: number): void;
  /** Call right before `renderer.render`. */
  beforeRender(camera: THREE.PerspectiveCamera, width: number, height: number, nowMs?: number): void;
  /** Call right after `renderer.render`, before anything else reads the camera. */
  afterRender(camera: THREE.PerspectiveCamera): void;
  dispose(): void;
}

function reducedMotion(): boolean {
  try {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch { return false; }
}

/**
 * The shake offset at `t` ms into a kick: a decaying jitter. Pure so a test can
 * hold the envelope (zero at the end, never past the peak).
 */
export function shakeOffset(t: number, strength: number, seed: number): { x: number; y: number } {
  if (t < 0 || t >= SHOT_SHAKE_MS) return { x: 0, y: 0 };
  const decay = 1 - t / SHOT_SHAKE_MS;
  const amp = SHOT_SHAKE_PX * Math.max(0, Math.min(1, strength)) * decay * decay;
  // Two incommensurate wobbles, so it shudders rather than slides.
  const a = seed + t * 0.09;
  return { x: amp * Math.sin(a * 2.3), y: amp * Math.cos(a * 3.1) };
}

export function createShotFeedback(host: HTMLElement): ShotFeedback {
  const blur = document.createElement('div');
  blur.className = 'bv-shot-blur';
  blur.setAttribute('aria-hidden', 'true');
  host.appendChild(blur);

  let kickAt = -Infinity;
  let strength = 1;
  let seed = 0;
  let offsetOn = false;
  // `setViewOffset` overwrites `camera.aspect` with width / height; put it back.
  let savedAspect = 1;

  return {
    kick(s = 1) {
      if (reducedMotion()) return;
      kickAt = performance.now();
      strength = s;
      seed = Math.random() * 100;
      // Restart the fade: drop the class, force a style flush, add it back.
      blur.classList.remove('bv-shot-blur-on');
      void blur.offsetWidth;
      blur.classList.add('bv-shot-blur-on');
    },
    beforeRender(camera, width, height, nowMs = performance.now()) {
      const { x, y } = shakeOffset(nowMs - kickAt, strength, seed);
      if (x === 0 && y === 0) return;
      savedAspect = camera.aspect;
      camera.setViewOffset(width, height, x, y, width, height);
      offsetOn = true;
    },
    afterRender(camera) {
      if (!offsetOn) return;
      camera.aspect = savedAspect;
      camera.clearViewOffset(); // recomputes the projection with the restored aspect
      offsetOn = false;
    },
    dispose() {
      blur.remove();
    },
  };
}
